import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { ITEM } from '../sim/items';
import type { DecodedSnapshot } from '../sim/protocol';

/**
 * World items as one small InstancedMesh per type (4 draw calls total).
 * Positions interpolate between snapshots by item id; apple/grime scale
 * shrinks with their state (bites left / scrub progress).
 */

function tag(geoIn: THREE.BufferGeometry, color: string): THREE.BufferGeometry {
  let geo = geoIn;
  if (geo.index !== null) {
    geo = geoIn.toNonIndexed();
    geoIn.dispose();
  }
  const count = geo.getAttribute('position').count;
  const c = new THREE.Color(color);
  const colors = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    // per-facet mottle: apples speckle, canopies leaf, grime lumps
    const face = (i / 3) | 0;
    let h = Math.imul(face + 1, 0x27d4eb2d) ^ 0x9e3779b9;
    h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
    const f = 1 + ((((h >>> 8) & 0xffff) / 65536 - 0.5) * 0.1);
    colors[i * 3] = c.r * f;
    colors[i * 3 + 1] = c.g * f;
    colors[i * 3 + 2] = c.b * f;
  }
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  return geo;
}

function buildApple(): THREE.BufferGeometry {
  const body = tag(new THREE.IcosahedronGeometry(0.17, 0), '#C13B2A');
  const stem = new THREE.ConeGeometry(0.03, 0.12, 4);
  stem.translate(0, 0.2, 0);
  const leaf = new THREE.ConeGeometry(0.06, 0.1, 4);
  leaf.rotateZ(1.2);
  leaf.translate(0.08, 0.2, 0);
  const merged = mergeGeometries([body, tag(stem, '#6B4A26'), tag(leaf, '#4E9A3E')], false);
  merged.translate(0, 0.17, 0);
  merged.computeVertexNormals();
  return merged;
}

function buildBall(): THREE.BufferGeometry {
  const geo = tag(new THREE.IcosahedronGeometry(0.26, 1), '#3B6FC1');
  // alternate faces white for the classic beach-ball read
  const colors = geo.getAttribute('color') as THREE.BufferAttribute;
  const white = new THREE.Color('#EDE8DC');
  for (let f = 0; f < colors.count / 3; f++) {
    if (f % 2 === 0) continue;
    for (let v = 0; v < 3; v++) {
      colors.setXYZ(f * 3 + v, white.r, white.g, white.b);
    }
  }
  geo.translate(0, 0.26, 0);
  geo.computeVertexNormals();
  return geo;
}

function buildGrime(): THREE.BufferGeometry {
  const geo = tag(new THREE.IcosahedronGeometry(0.4, 0), '#57513A');
  geo.scale(1, 0.35, 1);
  geo.translate(0, 0.1, 0);
  geo.computeVertexNormals();
  return geo;
}

function buildEgg(): THREE.BufferGeometry {
  const geo = tag(new THREE.IcosahedronGeometry(0.4, 1), '#F2EBDD');
  geo.scale(0.92, 1.22, 0.92);
  geo.translate(0, 0.48, 0);
  geo.computeVertexNormals();
  return geo;
}

function buildFeeder(): THREE.BufferGeometry {
  const trunk = new THREE.CylinderGeometry(0.14, 0.2, 1.3, 5);
  trunk.translate(0, 0.65, 0);
  const canopy = new THREE.IcosahedronGeometry(1.05, 0);
  canopy.scale(1, 0.85, 1);
  canopy.translate(0, 1.9, 0);
  const apple1 = new THREE.IcosahedronGeometry(0.13, 0);
  apple1.translate(0.55, 1.5, 0.5);
  const apple2 = new THREE.IcosahedronGeometry(0.13, 0);
  apple2.translate(-0.5, 1.7, -0.35);
  const merged = mergeGeometries(
    [tag(trunk, '#6B4A26'), tag(canopy, '#3F8433'), tag(apple1, '#C13B2A'), tag(apple2, '#C13B2A')],
    false,
  );
  merged.computeVertexNormals();
  return merged;
}

function buildSpring(): THREE.BufferGeometry {
  const rim = new THREE.CylinderGeometry(0.95, 1.1, 0.36, 7);
  rim.translate(0, 0.18, 0);
  const water = new THREE.CylinderGeometry(0.78, 0.78, 0.1, 7);
  water.translate(0, 0.38, 0);
  const merged = mergeGeometries([tag(rim, '#8A8D93'), tag(water, '#59A2D6')], false);
  merged.computeVertexNormals();
  return merged;
}

function buildTotem(): THREE.BufferGeometry {
  const a = new THREE.BoxGeometry(0.55, 0.5, 0.55);
  a.translate(0, 0.25, 0);
  const b = new THREE.BoxGeometry(0.45, 0.45, 0.45);
  b.rotateY(0.5);
  b.translate(0, 0.72, 0);
  const c = new THREE.BoxGeometry(0.36, 0.4, 0.36);
  c.rotateY(1);
  c.translate(0, 1.14, 0);
  const top = new THREE.ConeGeometry(0.3, 0.4, 4);
  top.translate(0, 1.54, 0);
  const merged = mergeGeometries(
    [tag(a, '#B4543A'), tag(b, '#D6A431'), tag(c, '#5B8BC6'), tag(top, '#B4543A')],
    false,
  );
  merged.computeVertexNormals();
  return merged;
}

interface TypedMesh {
  mesh: THREE.InstancedMesh;
  type: number;
  cap: number;
}

export class ItemRenderer {
  readonly group = new THREE.Group();
  private readonly meshes: TypedMesh[];
  private readonly typeToIdx: (number | undefined)[] = [];
  private readonly tmpMat = new THREE.Matrix4();
  private readonly tmpQuat = new THREE.Quaternion();
  private readonly tmpPos = new THREE.Vector3();
  private readonly tmpScale = new THREE.Vector3();
  private readonly prevIndex = new Map<number, number>();
  private prevTick = -1;
  /** id -> wobble seconds remaining (egg taps, grime jiggle) */
  private readonly wobbles = new Map<number, number>();
  private wobbleClock = 0;

  constructor() {
    const material = new THREE.MeshLambertMaterial({ vertexColors: true });
    const make = (geo: THREE.BufferGeometry, type: number, cap: number, name: string): TypedMesh => {
      const mesh = new THREE.InstancedMesh(geo, material, cap);
      mesh.count = 0;
      mesh.frustumCulled = false;
      mesh.name = name;
      this.group.add(mesh);
      return { mesh, type, cap };
    };
    this.meshes = [
      make(buildApple(), ITEM.APPLE, 16, 'items-apple'),
      make(buildBall(), ITEM.BALL, 4, 'items-ball'),
      make(buildGrime(), ITEM.GRIME, 24, 'items-grime'),
      make(buildEgg(), ITEM.EGG, 2, 'items-egg'),
      make(buildFeeder(), ITEM.FEEDER, 4, 'items-feeder'),
      make(buildSpring(), ITEM.SPRING, 4, 'items-spring'),
      make(buildTotem(), ITEM.TOTEM, 4, 'items-totem'),
    ];
    this.meshes.forEach((m, idx) => {
      this.typeToIdx[m.type] = idx;
    });
  }

  /** Kick a short wobble animation on an item (egg tap feedback). */
  wobble(id: number): void {
    this.wobbles.set(id, 0.45);
  }

  applyFrom(older: DecodedSnapshot, newer: DecodedSnapshot, alpha: number, dt: number): void {
    if (older.tick !== this.prevTick) {
      this.prevIndex.clear();
      for (let i = 0; i < older.itemCount; i++) {
        this.prevIndex.set(older.itemId[i] as number, i);
      }
      this.prevTick = older.tick;
    }
    this.wobbleClock += dt;
    for (const [id, left] of this.wobbles) {
      const next = left - dt;
      if (next <= 0) this.wobbles.delete(id);
      else this.wobbles.set(id, next);
    }

    const counts = [0, 0, 0, 0, 0, 0, 0];
    for (let i = 0; i < newer.itemCount; i++) {
      const type = newer.itemType[i] as number;
      const mIdx = this.typeToIdx[type];
      if (mIdx === undefined) continue;
      const tm = this.meshes[mIdx] as TypedMesh;
      const idx = counts[mIdx] as number;
      if (idx >= tm.cap) continue;
      counts[mIdx] = idx + 1;

      const id = newer.itemId[i] as number;
      let x = newer.itemX[i] as number;
      let z = newer.itemZ[i] as number;
      let y = newer.itemY[i] as number;
      const p = this.prevIndex.get(id);
      if (p !== undefined && alpha <= 1) {
        x = (older.itemX[p] as number) + (x - (older.itemX[p] as number)) * alpha;
        z = (older.itemZ[p] as number) + (z - (older.itemZ[p] as number)) * alpha;
        y = (older.itemY[p] as number) + (y - (older.itemY[p] as number)) * alpha;
      }

      const state = newer.itemState[i] as number;
      let s = 1;
      if (type === ITEM.APPLE) s = 0.55 + 0.45 * (state / 3);
      else if (type === ITEM.GRIME) {
        // a faint queasy pulse — reads as "active bad thing, scrub me"
        s = (0.45 + 0.55 * (state / 100)) * (1 + 0.05 * Math.sin(this.wobbleClock * 2.6 + id));
      }

      let rot = 0;
      const wob = this.wobbles.get(id);
      if (wob !== undefined) {
        rot = Math.sin(this.wobbleClock * 40) * 0.16 * (wob / 0.45);
      }

      this.tmpQuat.setFromAxisAngle(AXIS_Z, rot);
      this.tmpPos.set(x, y, z);
      this.tmpScale.setScalar(s);
      this.tmpMat.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
      tm.mesh.setMatrixAt(idx, this.tmpMat);
    }
    for (let m = 0; m < this.meshes.length; m++) {
      const tm = this.meshes[m] as TypedMesh;
      tm.mesh.count = counts[m] as number;
      tm.mesh.instanceMatrix.needsUpdate = true;
    }
  }
}

const AXIS_Z = new THREE.Vector3(0, 0, 1);
